'use client'

import { FC, useContext, useEffect, useState } from 'react'
import styles from './styles.module.scss'
import cx from 'classnames'
import { Colour } from '../constants'
import { CmsPageSectionBase, CmsResponsiveImage } from '../ComposedPage'
import { AnalyticsContext } from '@/hooks/useAnalytics'
import { useBlogRichText } from '../Blog/hooks/useBlogRichText'
import { RichTextContent } from '@/hooks/useRichText'
import { HeroCtas } from '../Sections/PageHero/HeroCtas'

import { DynamicImage } from '../DynamicImage'
import { Modal } from '../Modal'

export interface CmsDismissiblePopover extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    internalTitle?: string
    title?: string
    subtitle?: string
    richText?: RichTextContent | null
    showOpenAccountButton: boolean
    primaryCtaText?: string
    primaryCtaUrl?: string
    secondaryCtaText?: string
    secondaryCtaUrl?: string
    delayInSeconds: number
    image?: CmsResponsiveImage
    richTextListMarkerStyle?: string
  }
}

export const DismissiblePopover: FC<CmsDismissiblePopover> = ({
  fields,
  sys,
}) => {
  const {
    title,
    subtitle,
    richText = null,
    richTextListMarkerStyle = '',
    delayInSeconds,
    showOpenAccountButton,
    primaryCtaText,
    primaryCtaUrl,
    secondaryCtaText,
    secondaryCtaUrl,
    image,
  } = fields
  const contentful_id = sys.id
  const [show, setShow] = useState(false)
  const { consent_applied } = useContext(AnalyticsContext)

  const handleDisplay = () => {
    setShow(true)
  }

  const handleOnClose = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(contentful_id, 'viewed')
    }
    setShow(false)
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    const isDismissed = window.localStorage.getItem(contentful_id)
    let timeout: ReturnType<typeof setTimeout>

    if (!isDismissed && consent_applied) {
      timeout = setTimeout(handleDisplay, delayInSeconds * 1000)
    }

    return () => {
      window.clearTimeout(timeout)
    }
  }, [delayInSeconds, contentful_id, consent_applied])

  const richTextRender = useBlogRichText(richText || null)

  return (
    show && (
      <Modal
        data-testid="popover"
        onClose={handleOnClose}
        className={styles.dismissibleModal}
        id={`dismissible-${title}`}
      >
        <div className={cx(styles.dismissiblePopover)}>
          <div className={cx(styles.content, styles[Colour.warmStone])}>
            <div className={cx(styles.text, styles[richTextListMarkerStyle!])}>
              {title && <h2 className={styles.title}>{title}</h2>}
              {subtitle && <h5>{subtitle}</h5>}
              {richText && richTextRender}
              <HeroCtas
                showOpenAccountButton={showOpenAccountButton}
                primaryCtaText={primaryCtaText}
                primaryCtaUrl={primaryCtaUrl}
                secondaryCtaText={secondaryCtaText}
                secondaryCtaUrl={secondaryCtaUrl}
              />
            </div>
            <div className={styles.image}>
              {image && (
                <DynamicImage
                  image={image}
                  width={image.fields.file.details.image?.width}
                  height={image.fields.file.details.image?.height}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
    )
  )
}
